import { Input, message, Popover, Tooltip } from "antd";
import classNames from "classnames";
import { t } from "i18next";
import Image from "@components/Image";
import { useEffect, useState, useRef, type ReactNode } from "react";
import { Trans } from "react-i18next";
import { useDebounce, useHideElement } from "@hooks/utils";
import "./index.scss";
import TruncateTitle from "@components/TruncateTitle";
import Empty from "../Empty";
import {
  IconChangeLang,
  IconDelete,
  IconFolder,
  IconInputDel,
  IconLink,
  IconLoading,
  IconPage,
  IconProduct,
} from "@icons/index";
import CountrySelector from "../CountrySelector";
import {
  productSearch,
  productSearchByUrl,
  removeProductItem,
  removeSellingPoints,
  updateCountry,
  updateProductItem,
} from "@pages/aiStudio/ScriptRewrite/scriptRewriteSlice";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { Section } from "@src/ui/Section";
import { CommonButton } from "@components/CommonButton";
import { useVideoRewrite } from "@pages/aiStudio/ScriptRewrite/hooks";
import commonservice from "@apis/commonservice";
import { ensureHttps, stripProtocol } from "@pages/aiStudio/ScriptExport/insight";

interface SearchProductInputProps {
  disabled?: boolean;
  isOpen?: boolean;
  showEdit?: boolean;
  className?: string;
  popoverWidth?: number;
  handleShowModal?: (open: boolean) => void;
}
const inputTypeOptions = [
  { key: 0, icon: IconLink, label: "Product Link" },
  { key: 1, icon: IconProduct, label: "Product Name" },
];

const SearchProductInput = (props: SearchProductInputProps) => {
  const { disabled, isOpen, className, handleShowModal, popoverWidth } = props;
  const dispatch = useAppDispatch();
  const [inputWorld, setInputWorld] = useState<string>("");
  const [showProductSearch, setShowProductSearch] = useState(false);
  const { requestSellingPointers } = useVideoRewrite();
  const { productItem, productList, status, hasProduct, country } = useAppSelector(
    (state) => state.scriptRewriteReducer,
  );
  const [showType, setShowType] = useState<number>(0);
  const [translating, setTranslating] = useState(false);
  const handleSearchRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (productItem?.status === "succeeded") {
      setInputWorld("");
    }
  }, [productItem.status]);

  useHideElement("SearchProductInput", () => {
    setShowProductSearch(false);
  });

  const handleTranslate = async () => {
    if (!inputWorld.trim() || translating) return;
    setTranslating(true);
    try {
      const result = await commonservice.getTitleTranslate({ text: inputWorld, to: "en" });
      if (result) {
        setInputWorld(result);
        setShowProductSearch(true);
        dispatch(productSearch({ keyword: result, country }));
      }
    } catch (_) {
      message.error(t("Translation failed, please try again"));
    } finally {
      setTranslating(false);
    }
  };

  // URL 校验函数
  const isValidUrl = (string: string): boolean => {
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  // 处理搜索（回车或防抖触发）
  const handleSearch = async () => {
    if (showType === 0) {
      // URL 模式
      if (!inputWorld.trim()) return;
      const urlInput = ensureHttps(inputWorld);
      if (!isValidUrl(urlInput)) {
        message.warning(t("Please enter a valid URL"));
        return;
      }
      setShowProductSearch(false);

      const result: any = await dispatch(
        productSearchByUrl({
          keyword: urlInput,
        }),
      );
      const productId = result.payload?.[0]?.id;
      const productCountry = result.payload?.[0]?.country;
      if (productId) {
        requestSellingPointers(productId, productCountry);
      }
    } else {
      // 商品搜索模式
      if (!inputWorld.trim()) return;
      setShowProductSearch(true);
      dispatch(
        productSearch({
          keyword: inputWorld,
          country: country || "US",
        }),
      );
    }
  };

  handleSearchRef.current = handleSearch;
  const debouncedSearch = useDebounce(() => handleSearchRef.current(), 500);

  const renderProductSearchContent = () => {
    // Trans 会把插值内容作为 children 传给根节点，所以用包装组件把 children 放进带颜色的 span 里
    const ProductTitlePart = ({ children }: { children?: ReactNode }) => (
      <Tooltip title={inputWorld}>
        <span className="font-normal line-clamp-1 text-ellipsis" style={{ color: "var(--kds-accent)" }}>
          {children}
        </span>
      </Tooltip>
    );

    return (
      <div
        className={classNames("absolute z-10 mt-[2px] w-full h-[360px] kalo-popover flex flex-col", className)}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {inputWorld.length > 0 ? (
          <Section
            className={classNames(
              "py-[16px] px-[12px] overflow-y-scroll h-[190px] gap-[8px]",
              productList?.status === "loading" ? "justify-center" : "justify-start",
            )}
            status={productList.status}
            error={productList.error}
          >
            {inputWorld && (
              <div
                className="p-[4px] items-center gap-[4px] rounded-[4px] kalo-tag inline-flex pr-[10px] whitespace-nowrap cursor-pointer font-[13px]"
                onClick={() => {
                  dispatch(updateProductItem({ productTitle: inputWorld }));
                  dispatch(removeSellingPoints());
                  setShowProductSearch(false);
                }}
              >
                <IconPage className="min-w-[16px] min-h-[16px]" />
                <Trans
                  i18nKey="product_title"
                  defaults={`Use "<1>{{product_title}}</1>" as the product title`}
                  values={{ product_title: inputWorld }}
                  components={{ 1: <ProductTitlePart /> }}
                ></Trans>
              </div>
            )}
            {(productList?.data || []).map((item: any) => (
              <div
                key={item.id}
                className="flex items-center gap-[8px] rounded-[4px] p-[4px] cursor-pointer transition-all kalo-tag"
                onClick={() => {
                  dispatch(updateProductItem(item));
                  setShowProductSearch(false);
                  requestSellingPointers(item.id, item.country);
                }}
              >
                <div className="w-[32px] h-[32px] rounded-[4px] border border-kds-border overflow-hidden">
                  <Image className="w-[32px] h-[32px]" url={item.coverImageUrl} hoverEnlarge={true}></Image>
                </div>
                <TruncateTitle
                  className="flex-1"
                  text={item?.productTitle}
                  lineclamp2
                  placement="top"
                  highLightWord={inputWorld}
                  getPopupContainer={(node) => node.parentElement!}
                ></TruncateTitle>
              </div>
            ))}
          </Section>
        ) : (
          <Empty image="8903ffad.png" tips="No Result" size="small" card={false} />
        )}
      </div>
    );
  };

  return (
    <section className="mb-[16px]relative SearchProductInput">
      <div className="flex items-center justify-between gap-[4px] mb-[8px]">
        <div className="font-semibold text-[16px]">
          <Trans>Product to Promote</Trans>
        </div>
        {!(disabled && status !== "idle") && (
          <div
            className="flex items-center gap-[4px] text-kds-fg-soft cursor-pointer"
            onClick={() => {
              handleShowModal && handleShowModal(true);
            }}
          >
            <IconFolder className="w-[16px] h-[16px] text-black" />
            <Trans>Add from library</Trans>
          </div>
        )}
      </div>

      {/* 商品框 */}
      {productItem?.data ? (
        <Section
          status={productItem.status}
          error={productItem.error}
          className="bg-kds-bg-card border border-kds-border-subtle rounded-[8px] h-[72px]"
        >
          <div
            className={classNames(
              "flex items-center gap-[8px] p-[8px] transition-all",
              !disabled && "SearchProductInputBoxEffects",
            )}
            onClick={() => {
              if (disabled) return;
              setShowProductSearch(!showProductSearch);
            }}
          >
            <div className="w-[55px] h-[55px] rounded-[4px] border border-kds-border overflow-hidden">
              <Image
                className="w-[54px] h-[54px]"
                url={productItem?.data?.coverImageUrl || "https://d149xzut2sq6e3.cloudfront.net/upload/f61bea2b.png"}
                hoverEnlarge={true}
              ></Image>
            </div>
            <TruncateTitle
              className="flex-1"
              text={productItem?.data?.productTitle}
              lineclamp2
              placement="top"
            ></TruncateTitle>
            {!disabled && (
              <div
                className={classNames(
                  "flex flex-nowrap items-center gap-[2px] text-kds-accent icon-del transition-all cursor-pointer",
                  isOpen && "icon-del-visible",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(removeProductItem());
                }}
              >
                <IconDelete className="w-[16px] h-[16px]" />
              </div>
            )}
          </div>
        </Section>
      ) : (
        <Popover
          content={renderProductSearchContent}
          trigger={"click"}
          placement="bottom"
          arrow={false}
          open={showType === 1 && showProductSearch}
          align={{ offset: [0, 0] }}
          // @ts-expect-error status prop not in PopoverProps
          status
          overlayStyle={{ width: popoverWidth || "auto" }}
          overlayClassName="SearchProductInput-popover"
        >
          <Input
            value={inputWorld}
            placeholder={
              showType === 0 ? t("Paste TikTok, Amazon or Kalodata product link") : t("Enter product name to search")
            }
            className="max-w-[800px] h-[56px]"
            size="large"
            disabled={productItem.status === "loading" || status === "loading"}
            onFocus={() => {
              if (showType === 1 && inputWorld.trim()) {
                setShowProductSearch(true);
              }
            }}
            onKeyDown={(e) => {
              if (productItem.status === "loading" || status === "loading") return;
              if ((e.code === "Enter" || e.code === "NumpadEnter") && e.keyCode !== 229) {
                debouncedSearch();
              }
            }}
            prefix={
              <div className="flex items-center">
                {/* 切换输入框类型 */}
                <div className="toggle-group">
                  {inputTypeOptions.map((option) => (
                    <Tooltip key={option.key} title={t(option.label)}>
                      <div
                        className={classNames("toggle-item", showType === option.key && "active")}
                        onClick={() => {
                          setShowType(option.key);
                          setInputWorld("");
                          setShowProductSearch(false);
                        }}
                      >
                        <option.icon className="w-[20px] h-[20px]" />
                      </div>
                    </Tooltip>
                  ))}
                </div>

                {showType === 0 ? (
                  <div className="font-semibold text-[14px] text-kds-fg flex items-center pl-[12px]">
                    <span>Https://</span>
                    <div className="w-px h-[48px] bg-kds-border-solid ml-[8px] mr-[4px]"></div>
                  </div>
                ) : (
                  <>
                    <CountrySelector
                      onChange={(e: any) => {
                        dispatch(updateCountry(e));
                        if (inputWorld.trim()) debouncedSearch();
                      }}
                      prefixValue={country}
                    />
                    <div className="w-px h-[48px] bg-kds-border-solid ml-[8px] mr-[4px]"></div>
                  </>
                )}
              </div>
            }
            allowClear={{ clearIcon: <IconInputDel className="w-[16px] h-[16px]" /> }}
            suffix={
              showType === 1 ? (
                <CommonButton
                  autoPath={false}
                  className="w-[32px]"
                  disabled={
                    !inputWorld.trim() || translating || productItem.status === "loading" || status === "loading"
                  }
                  icon={
                    <>
                      {translating ? (
                        <IconLoading className="animate-spin icon" />
                      ) : (
                        <IconChangeLang className="w-[20px] h-[20px]" />
                      )}
                    </>
                  }
                  onClick={handleTranslate}
                />
              ) : productItem.status === "loading" ? (
                <IconLoading className="animate-spin icon" />
              ) : null
            }
            onChange={(e) => {
              const val = showType === 0 ? stripProtocol(e.target.value) : e.target.value;
              setInputWorld(val);
              if (val.trim()) {
                debouncedSearch();
              }
            }}
          />
        </Popover>
      )}
      {!productItem?.data && showType === 0 && productItem.status === "failed" && (
        <div className="text-kds-danger mt-[8px] text-[12px]">
          * {t(productItem.error || "Product not found, Please re-enter it.")}
        </div>
      )}
      {hasProduct && !productItem?.data && productItem.status !== "failed" && (
        <div className="text-kds-danger mt-[8px] text-[12px]">
          * <Trans>This is a promotional video. Please select a product.</Trans>
        </div>
      )}
    </section>
  );
};

export default SearchProductInput;
